import {
  addClass,
  removeClass,
  getTallestElement,
  matchHeight,
  shrinkAccordionItems,
  toggleFloatPanels,
  toggleAccordionItem,
} from './common';

const defaultCssClass = 'e-acc';
const clzFloatItemsWrapper = `.${defaultCssClass}__flt-itms-wrpr`;
const clzFloatItemWrapper = `.${defaultCssClass}__flt-itm-wrpr`;
const clzFloatItem = `.${defaultCssClass}__flt-itm`;
const clzItems = `.${defaultCssClass}__itm-wrpr`;
const clzItemBody = `.${defaultCssClass}__itm-bdy`;
const activeClass = 'active';
const initClass = 'initialized';

// --- Video Handler Utilities ---
const handleVideoPlay = ( panel ) => {
  if ( !panel ) return;
  const video = panel.querySelector( 'video' );
  if ( video ) {
    video.play().catch( () => {} );
    return;
  }
  const iframe = panel.querySelector( 'iframe[src*="wistia"]' );
  if ( iframe && window.Wistia ) {
    window._wq = window._wq || [];
    window._wq.push( {
      id: iframe.id || '_all',
      onReady: function ( video ) {
        video.play();
      }
    } );
  }
};

const handleVideoPause = ( panel ) => {
  if ( !panel ) return;
  const video = panel.querySelector( 'video' );
  if ( video ) {
    const wasPlaying = !video.paused;
    video.pause();
    // Only reset currentTime if the video was actually playing
    if ( wasPlaying ) {
      video.currentTime = 0;
    }
    return;
  }
  const iframe = panel.querySelector( 'iframe[src*="wistia"]' );
  if ( iframe && window.Wistia ) {
    window._wq = window._wq || [];
    window._wq.push( {
      id: iframe.id || '_all',
      onReady: function ( video ) {
        video.pause();
        video.time( 0 );
      }
    } );
  }
};

const pauseAllFloatPanelVideos = ( floatPanels ) => {
  floatPanels.forEach( panel => handleVideoPause( panel ) );
};

const playActivePanelVideo = ( floatPanels, activeId ) => {
  const panel = floatPanels.find( el => el.dataset.id === activeId );
  if ( panel ) handleVideoPlay( panel );
};
// --- End Video Handler Utilities ---

const debounce = ( func, delay ) => {
  let timerId;
  return function ( ...args ) {
    if ( timerId ) clearTimeout( timerId );
    timerId = setTimeout( () => func.apply( this, args ), delay );
  };
};

export const appendPanel = (
  acc,
  items,
  floatPanelsWrapper,
  floatPannels,
  settings,
  mobileView,
) => {
  const { autoSlide } = settings;

  if ( mobileView ) {
    items.forEach( el => {
      floatPannels.forEach( flEl => {
        const id = flEl.dataset.id;
        if (
          el.querySelector( '[data-toggle="collapse"]' ).dataset.target === id
        ) {
          el.querySelector( clzItemBody ).after( flEl );
          removeClass( acc, 'column' );
          if ( autoSlide ) removeClass( acc, 'auto-slide' );
        }
      } );
    } );
  } else {
    floatPannels.forEach( flEl => {
      floatPanelsWrapper.appendChild( flEl );
      addClass( acc, 'column' );
      if ( autoSlide ) addClass( acc, 'auto-slide' );
    } );
  }
};

const floatPanelWrapperSetHeight = ( floatPanelsWrapper, floatPannelInner ) => {
  const height = getTallestElement( floatPannelInner );
  floatPanelsWrapper.style.minHeight = height + 'px';
};

export const toggleAccordion = (
  handles,
  settings,
  floatPannels,
  items,
  smoothSlide,
  onUserInteraction = null,
) => {
  const { toggleMode, floatPanel } = settings;

  handles.forEach( handle => {
    handle.addEventListener( 'click', el => {
      const id = el.currentTarget.dataset.target;
      const filterActiveItems = items.filter(
        i => i.classList.contains( activeClass ) && i.dataset.itemId !== id,
      );
      
      // Check if the clicked item is already active
      const clickedItem = items.find( item => item.dataset.itemId === id );
      const isAlreadyActive = clickedItem?.classList.contains( activeClass ) ?? false;
      
      if ( toggleMode ) {
        shrinkAccordionItems( filterActiveItems, smoothSlide );
      }
      toggleAccordionItem( id, smoothSlide );
      if ( floatPanel ) {
        toggleFloatPanels( floatPannels, id );
      }
      
      // --- Video Handling on item click ---
      // Only pause and restart videos if the item wasn't already active
      if ( !isAlreadyActive ) {
        pauseAllFloatPanelVideos( floatPannels );
        playActivePanelVideo( floatPannels, id );
      }
      
      // Track user interaction if callback is provided
      if ( onUserInteraction ) {
        onUserInteraction();
      }
    } );
  } );
};

export const autoPlayAccordion = (
  accordions,
  settings,
  slides,
  items,
  userInteractionState,
  floatPannels, // pass float panels here!
  smoothSlide = false
) => {
  const state = {
    set: null,
    count: getInitialSlideIndex( slides ),
    areaFocus: false,
    autoplayEnabled: true,
  };

  const { autoPlayInterval, colBreakPoint, pauseOnFocus } = settings;
  const interval = autoPlayInterval || 3000;
  const slidesCount = slides.length;

  const disableAutoPlayMobile = () =>
    window.matchMedia( `(max-width: ${colBreakPoint}px)` ).matches;

  const initialize = () => {
    if ( !state.autoplayEnabled || disableAutoPlayMobile() || userInteractionState.hasInteracted ) return;
    accordions.classList.remove( 'autoplay-stopped' );
    state.set = setInterval( next, interval );
  };

  const next = () => {
    clearInterval( state.set );
    updateSlides( slides, state.count, slidesCount, smoothSlide, floatPannels );
    state.count = ( state.count + 1 ) % slidesCount;
    initialize();
  };

  const stopAutoplay = () => {
    clearInterval( state.set );
    state.autoplayEnabled = false;
    userInteractionState.setInteracted();
  };

  const handleClick = targetId => {
    if ( disableAutoPlayMobile() ) return;
    stopAutoplay();
    updateOnClick( slides, state, targetId, floatPannels );
    if ( !state.areaFocus ) {
      initialize();
    }
  };

  const getRootMargin = () => ( window.innerWidth > 2500 ? '-35% 0px -65%' : '-50% 0px -50%' );

  const observerOptions = {
    root: null,
    rootMargin: getRootMargin(),
    threshold: 0
  };

  const observer = new IntersectionObserver( entries => {
    entries.forEach( entry => {
      if ( entry.isIntersecting ) {
        accordions.classList.add( 'in-view' );
        accordions.classList.remove( 'autoplay-reset' );
        setupEventListeners( accordions, state, pauseOnFocus, initialize );
        setupItemClickHandlers( items, handleClick );
        if ( !userInteractionState.hasInteracted ) {
          initialize();
        } else {
          accordions.classList.add( 'autoplay-stopped' );
        }
        // --- Play video for first active item on view ---
        const active = floatPannels.find( p => p.classList.contains( 'active' ) );
        pauseAllFloatPanelVideos( floatPannels );
        if ( active ) handleVideoPlay( active );
      } else {
        accordions.classList.remove( 'in-view' );
        accordions.classList.add( 'autoplay-reset' );
        clearInterval( state.set );
        pauseAllFloatPanelVideos( floatPannels );
      }
    } );
  }, observerOptions );

  observer.observe( accordions );
};

const getInitialSlideIndex = slides => {
  const activeSlide = slides.filter( f => f.classList.contains( 'active' ) );
  return slides.findIndex( item => item.dataset.id === activeSlide[0]?.dataset.id );
};

const handleFadeIn = el => {
  el.style.opacity = '1';
  addClass( el );
};

const handleFadeOut = el => {
  el.style.opacity = '0';
  removeClass( el );
};

const updateSlides = ( slides, currentCount, slidesCount, smoothSlide, floatPannels ) => {
  handleFadeOut( slides[currentCount] );
  toggleAccordionItem( slides[currentCount].dataset.id, smoothSlide );
  pauseAllFloatPanelVideos( floatPannels );

  const nextCount = ( currentCount + 1 ) % slidesCount;
  handleFadeIn( slides[nextCount] );
  toggleAccordionItem( slides[nextCount].dataset.id, smoothSlide );

  playActivePanelVideo( floatPannels, slides[nextCount].dataset.id );
};

const toggleHandleClass = id => {
  if ( !id ) return;
  const items = document.querySelectorAll( clzItems );
  items.forEach( removeClass );
  const targetItem = Array.from( items ).find( item => item.dataset.itemId === id );
  if ( targetItem ) {
    addClass( targetItem, activeClass );
  }
};

const updateOnClick = ( slides, state, targetId, floatPannels ) => {
  state.count = slides.findIndex( id => id.dataset.id === targetId );
  
  // Check if the target slide is already active
  const targetSlide = slides[state.count];
  const isAlreadyActive = targetSlide?.classList.contains( activeClass ) ?? false;
  
  
  slides.forEach( handleFadeOut );
  handleFadeIn( slides[state.count] );
  toggleHandleClass( targetId );

  // Only pause and restart videos if the target wasn't already active
  if ( !isAlreadyActive ) {
    pauseAllFloatPanelVideos( floatPannels );
    playActivePanelVideo( floatPannels, targetId );
  }
};

const setupEventListeners = ( accordions, state, pauseOnFocus, initialize ) => {
  if ( !pauseOnFocus ) return;

  accordions.addEventListener( 'mouseenter', () => {
    clearInterval( state.set );
    state.areaFocus = true;
  } );

  accordions.addEventListener( 'mouseleave', () => {
    state.areaFocus = false;
    if ( state.autoplayEnabled ) {
      initialize();
    }
  } );
};

const setupItemClickHandlers = ( items, handleClick ) => {
  items.forEach( item => {
    item.addEventListener( 'click', () => {
      const targetId = item.dataset.itemId;
      if ( targetId ) handleClick( targetId );
    } );
  } );
};

const setMatchHeight = acc => {
  matchHeight( acc.querySelectorAll( clzItemBody ) );
};

// ---- Cognitive Complexity Reduction ----

const setAutoplaySpeed = ( acc, settings ) => {
  if ( settings.autoPlayInterval ) {
    const speed = parseInt( settings.autoPlayInterval, 10 );
    acc.style.setProperty( '--autoplay-speed', `${speed}ms` );
  }
};

const setupUserInteractionState = ( acc ) => {
  const userInteractionState = {
    hasInteracted: false,
    setInteracted: () => {
      userInteractionState.hasInteracted = true;
      acc.classList.add( 'autoplay-stopped' );
    }
  };
  return userInteractionState;
};

const setupStopAutoplayOnItemClick = ( items, userInteractionState ) => {
  items.forEach( item => {
    item.addEventListener( 'click', () => {
      if ( !userInteractionState.hasInteracted ) {
        userInteractionState.setInteracted();
      }
    } );
  } );
};

const playInitialActivePanelVideo = ( floatPannels ) => {
  setTimeout( () => {
    const active = floatPannels.find( p => p.classList.contains( 'active' ) );
    pauseAllFloatPanelVideos( floatPannels );
    if ( active ) handleVideoPlay( active );
  }, 200 );
};

// Now, handleAccordion only takes ONE options object!
const handleAccordion = ( {
  acc,
  hasSmooth,
  settings,
  items,
  floatPanelsWrapper,
  floatPannels,
  floatPannelInner,
  handles,
  mobileView
} ) => {
  setAutoplaySpeed( acc, settings );
  const userInteractionState = setupUserInteractionState( acc );
  setupStopAutoplayOnItemClick( items, userInteractionState );

  if ( settings.floatPanel ) {
    appendPanel( acc, items, floatPanelsWrapper, floatPannels, settings, mobileView );
    floatPanelWrapperSetHeight( floatPanelsWrapper, floatPannelInner );
  }
  if ( settings.autoSlide && !mobileView ) {
    autoPlayAccordion( acc, settings, floatPannels, items, userInteractionState, floatPannels, hasSmooth );
  }
  toggleAccordion( handles, settings, floatPannels, items, hasSmooth, userInteractionState.setInteracted );
  setMatchHeight( acc );
  playInitialActivePanelVideo( floatPannels );
};

const extractAccordions = () => {
  const accordions = document.querySelectorAll( '[data-accordion]' );
  if ( accordions.length <= 0 ) return;

  accordions.forEach( acc => {
    if ( acc.classList.contains( initClass ) ) return;
    acc.classList.add( initClass );

    const hasSmooth = acc.classList.contains( 'smooth-slide' );
    const settings = JSON.parse( acc.dataset.settings );
    const items = Array.from( acc.querySelectorAll( clzItems ) );
    const floatPanelsWrapper = acc.querySelector( clzFloatItemsWrapper );
    const floatPannels = Array.from( acc.querySelectorAll( clzFloatItemWrapper ) );
    const floatPannelInner = Array.from( acc.querySelectorAll( clzFloatItem ) );
    const handles = Array.from( acc.querySelectorAll( '[data-target]' ) );
    const mobileView = window.matchMedia( `(max-width: ${settings.colBreakPoint}px)` ).matches;

    handleAccordion( {
      acc,
      hasSmooth,
      settings,
      items,
      floatPanelsWrapper,
      floatPannels,
      floatPannelInner,
      handles,
      mobileView
    } );
  } );
};

// ---- End Cognitive Complexity Reduction ----

let currentWidth = window.innerWidth;

( ( { hooks: { addFilter: addFilterFe } } ) => {
  // Frontend Hooks
  addFilterFe(
    'b3rg_accordion_filter',
    'b3rg/accordion',
    () => {
      const debounceSetTab = debounce( () => {
        if ( window.innerWidth !== currentWidth ) {
          currentWidth = window.innerWidth;
          extractAccordions();
        }
      }, 600 );

      window.addEventListener( 'load', () => {
        extractAccordions();
        window.addEventListener( 'resize', debounceSetTab );
      } );
    }
  );
  // eslint-disable-next-line no-undef
} )( feWp );
